import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ConfirmDialog } from '@/components/ConfirmDialog'
import type { CanopyDocument } from '@/lib/api/endpoints/documents'
import { isApiError } from '@/lib/api/errors'
import { cx } from '@/lib/browser/class-names'
import { useAuth } from '@/lib/auth/auth-context'
import {
  canDeleteDocuments,
  canDownloadDocuments,
  canRescoreCompany,
  canUploadDocuments,
} from '@/lib/auth/roles'
import { formatBytes } from '@/lib/documents/accepted-types'
import { useCompanies } from '@/pages/company/use-companies'

import { UploadZone } from './UploadZone'
import { downloadDocument, StorageError, useDocumentDetail, useDocuments } from './use-documents'
import { useResearchTrigger } from './use-research'
import { useDeleteDocument } from './use-upload'

interface FileTypeStyle {
  background: string
  color: string
  label: string
}

function fileTypeStyle(doc: {
  mimeType?: string | null | undefined
  fileName: string
}): FileTypeStyle {
  const mime = (doc.mimeType ?? '').toLowerCase()
  const name = doc.fileName.toLowerCase()

  const is = (...endings: string[]) => endings.some((ending) => name.endsWith(ending))

  if (mime.includes('pdf') || is('.pdf'))
    return { background: 'var(--red-50)', color: 'var(--red-600)', label: 'PDF' }

  if (
    mime.includes('excel') ||
    mime.includes('sheet') ||
    mime.includes('csv') ||
    is('.xlsx', '.xls', '.csv')
  )
    return { background: 'var(--green-50)', color: 'var(--green-800)', label: 'XLS' }

  if (mime.includes('presentation') || is('.ppt', '.pptx'))
    return { background: 'var(--amber-50)', color: 'var(--amber-800)', label: 'PPT' }

  if (mime.includes('word') || mime.includes('document') || is('.doc', '.docx'))
    return { background: 'var(--blue-50)', color: 'var(--blue-800)', label: 'DOC' }

  if (mime.startsWith('image/') || is('.png', '.jpg', '.jpeg', '.gif', '.webp'))
    return { background: 'var(--amber-50)', color: 'var(--amber-800)', label: 'IMG' }

  if (is('.txt')) return { background: 'var(--gray-100)', color: 'var(--text-2)', label: 'TXT' }
  if (is('.md', '.markdown'))
    return { background: 'var(--gray-100)', color: 'var(--text-2)', label: 'MD' }

  return { background: 'var(--gray-100)', color: 'var(--text-3)', label: 'FILE' }
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null

  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function downloadFailureMessage(error: unknown): string {
  if (error instanceof StorageError) {
    if (error.status === undefined) {
      return 'Your browser could not reach storage to fetch that file.'
    }

    const detail =
      error.code === undefined ? String(error.status) : `${error.code}, ${String(error.status)}`

    return `Storage refused that file (${detail}).`
  }

  if (!isApiError(error)) {
    return 'Could not reach the server to open that document. Check your connection and try again.'
  }

  switch (error.status) {
    case 403:
      return 'Your role does not allow downloading this document.'
    case 404:
      return 'That document no longer exists. The list may be out of date — reload the page.'
    case 500:
      return 'Storage is not reachable for this document. If this persists, the file may need re-uploading.'
    default:
      return `Could not open that document (${String(error.status)}).`
  }
}

export function Files() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [companyId, setCompanyIdState] = useState<string | undefined>(
    () => searchParams.get('companyId') ?? undefined,
  )

  const [confirmDelete, setConfirmDelete] = useState<CanopyDocument | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [directUrl, setDirectUrl] = useState<string | null>(null)

  function setCompanyId(next: string | undefined) {
    setCompanyIdState(next)
    setSearchParams(
      (params) => {
        const updated = new URLSearchParams(params)
        if (next === undefined) updated.delete('companyId')
        else updated.set('companyId', next)
        return updated
      },
      { replace: true },
    )
  }

  const remove = useDeleteDocument()

  const { session } = useAuth()
  const canUpload = canUploadDocuments(session?.role ?? null)

  const canDelete = canDeleteDocuments(session?.role ?? null)

  const canDownload = canDownloadDocuments()

  const canRescore = canRescoreCompany(session?.role ?? null)
  const research = useResearchTrigger(companyId)

  const [indexingNotice, setIndexingNotice] = useState<string | null>(null)

  const companyList = useCompanies()
  const documents = useDocuments(companyId)

  const companies = useMemo(
    () => [...(companyList.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [companyList.data],
  )

  const nameById = useMemo(() => {
    const names = new Map<string, string>()
    for (const company of companies) names.set(company.id, company.name)
    return names
  }, [companies])

  const countsByCompany = useMemo(() => {
    const counts = new Map<string, number>()
    for (const doc of documents.data?.items ?? []) {
      counts.set(doc.companyId, (counts.get(doc.companyId) ?? 0) + 1)
    }
    return counts
  }, [documents.data])

  async function download(documentId: string, fileName: string) {
    setDownloadError(null)
    setDirectUrl(null)

    try {
      await downloadDocument(documentId, fileName)
    } catch (error) {
      setDownloadError(downloadFailureMessage(error))
      if (error instanceof StorageError) setDirectUrl(error.url)
    }
  }

  const activeCompanyName =
    companyId === undefined ? 'All files' : (nameById.get(companyId) ?? 'Files')
  const shownCount = documents.data?.items.length ?? 0

  return (
    <div className="canopy-page">
      <div className="canopy-page-header">
        <div>
          <h1 className="canopy-page-title">Files</h1>
          <div className="canopy-page-sub">
            {documents.data
              ? `${String(documents.data.total)} file${documents.data.total === 1 ? '' : 's'} uploaded across ${String(companies.length)} compan${companies.length === 1 ? 'y' : 'ies'}`
              : 'Files uploaded across the portfolio'}
          </div>
        </div>
      </div>

      {downloadError !== null && (
        <div role="alert" className="mb-4 rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {downloadError}
          {directUrl !== null && (
            <>
              {' '}
              <a href={directUrl} target="_blank" rel="noopener noreferrer" className="underline">
                Open it directly
              </a>{' '}
              — the link is signed and expires in five minutes.
            </>
          )}
        </div>
      )}

      {indexingNotice !== null && (
        <div className="canopy-research-banner failed" role="alert">
          <span>{indexingNotice}</span>
          <button
            type="button"
            className="canopy-research-dismiss"
            onClick={() => {
              setIndexingNotice(null)
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="canopy-files-layout">
        <div className="canopy-files-sidebar">
          <div className="canopy-files-sidebar-header">Companies</div>
          <div className="canopy-files-company-list">
            <div
              className={cx('canopy-files-company-item', companyId === undefined && 'active')}
              onClick={() => {
                setCompanyId(undefined)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setCompanyId(undefined)
                }
              }}
              role="button"
              tabIndex={0}
            >
              <span>All companies</span>
              {documents.data && (
                <span className="canopy-files-company-count">{documents.data.total}</span>
              )}
            </div>
            {companies.map((company) => (
              <div
                key={company.id}
                className={cx('canopy-files-company-item', companyId === company.id && 'active')}
                onClick={() => {
                  setCompanyId(company.id)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setCompanyId(company.id)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span>{company.name}</span>
                {countsByCompany.has(company.id) && (
                  <span className="canopy-files-company-count">
                    {countsByCompany.get(company.id)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="canopy-files-main">
          {canUpload && (
            <UploadZone
              companies={companies}
              companyId={companyId}
              onUploaded={({ companyId: target, files, uploaded }) => {
                setIndexingNotice(null)

                if (!canRescore) return

                if (files.length === 0) {
                  setIndexingNotice(
                    `Uploaded ${String(uploaded)} file${uploaded === 1 ? '' : 's'}, but indexing could not start: the upload response carried no S3 key. Scoring will not see ${uploaded === 1 ? 'it' : 'them'} until this is fixed.`,
                  )
                  return
                }

                research.sync({
                  companyId: target,
                  companyName: nameById.get(target) ?? target,
                  files,
                })
              }}
            />
          )}

          <div className="canopy-files-list-card">
            <div className="canopy-files-list-header">
              <div className="canopy-section-title" style={{ marginBottom: 0 }}>
                {activeCompanyName}{' '}
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-3)',
                    fontWeight: 400,
                    marginLeft: 8,
                  }}
                >
                  {shownCount} file{shownCount === 1 ? '' : 's'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>newest first</span>
              </div>
            </div>

            {documents.isPending ? (
              <div className="canopy-empty-state">Loading…</div>
            ) : documents.isError ? (
              <div className="canopy-empty-state" role="alert">
                Could not load documents.
              </div>
            ) : documents.data.items.length === 0 ? (
              <div className="canopy-empty-state">
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--text-2)',
                    marginBottom: 4,
                  }}
                >
                  No files yet
                </div>
                <div>
                  Upload board materials, AI inventories, or diligence docs to associate them with a
                  company.
                </div>
              </div>
            ) : (
              documents.data.items.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  document={doc}
                  companyName={
                    companyId === undefined
                      ? (doc.companyName ?? nameById.get(doc.companyId))
                      : undefined
                  }
                  onDownload={
                    canDownload
                      ? () => {
                          void download(doc.id, doc.fileName)
                        }
                      : undefined
                  }
                  onDelete={
                    canDelete
                      ? () => {
                          setConfirmDelete(doc)
                        }
                      : undefined
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title={`Delete ${confirmDelete?.fileName ?? 'document'}?`}
        confirmLabel="Delete document"
        busy={remove.isPending}
        body="The document is removed from this application and its file is deleted from storage. The bucket is versioned, so a mistake is recoverable server-side — but not from here."
        onCancel={() => {
          setConfirmDelete(null)
          remove.reset()
        }}
        onConfirm={() => {
          if (!confirmDelete) return
          remove.mutate(confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null)
            },
          })
        }}
      />
    </div>
  )
}

const PARSE_PILL: Record<string, { background: string; color: string; text: string }> = {
  pending: { background: 'var(--gray-100)', color: 'var(--text-3)', text: 'pending' },
  parsing: { background: 'var(--amber-50)', color: 'var(--amber-800)', text: 'parsing…' },
  stored: { background: 'var(--green-50)', color: 'var(--green-800)', text: 'parsed' },
  complete: { background: 'var(--green-50)', color: 'var(--green-800)', text: 'parsed' },
  failed: { background: 'var(--red-50)', color: 'var(--red-600)', text: 'failed' },
}

function ParseStatusPill({ status }: { status: string }) {
  const pill = PARSE_PILL[status] ?? {
    background: 'var(--gray-100)',
    color: 'var(--text-3)',
    text: status,
  }

  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 500,
        padding: '1px 7px',
        borderRadius: 4,
        background: pill.background,
        color: pill.color,
      }}
    >
      {pill.text}
    </span>
  )
}

function DocumentRow({
  document,
  companyName,
  onDownload,
  onDelete,
}: {
  document: CanopyDocument
  companyName: string | undefined
  onDownload?: (() => void) | undefined
  onDelete?: (() => void) | undefined
}) {
  const type = fileTypeStyle(document)

  const failed = document.parseStatus === 'failed'
  const detail = useDocumentDetail(failed ? document.id : null)
  const parseError = document.parseError ?? detail.data?.parseError ?? null

  const facts = [
    companyName,
    typeof document.sizeBytes === 'number' ? formatBytes(document.sizeBytes) : null,
    formatDate(document.uploadedAt),
  ].filter((fact): fact is string => typeof fact === 'string' && fact !== '')

  return (
    <div className="canopy-file-row">
      <div className="canopy-file-icon" style={{ background: type.background, color: type.color }}>
        {type.label}
      </div>
      <div className="canopy-file-info">
        <div className="canopy-file-name">{document.fileName}</div>
        <div className="canopy-file-meta">
          {facts.join(' · ')}
          {document.parseStatus ? (
            <>
              {facts.length > 0 && ' · '}
              <ParseStatusPill status={document.parseStatus} />
            </>
          ) : null}
        </div>
        {document.periodCovered ? (
          <div className="canopy-file-notes">{document.periodCovered}</div>
        ) : null}
        {failed && parseError !== null && (
          <div style={{ fontSize: 11, color: 'var(--red-600)', marginTop: 2 }}>
            Parse failed: {parseError}
          </div>
        )}
      </div>
      {onDownload && (
        <button
          type="button"
          className="canopy-file-download"
          aria-label={`Download ${document.fileName}`}
          title="Download file"
          onClick={onDownload}
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              d="M8 2v8m0 0L5 7m3 3 3-3M3 12v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          className="canopy-file-delete"
          aria-label={`Delete ${document.fileName}`}
          title="Delete file"
          onClick={onDelete}
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 9h8l1-9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
